import { EditOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../../../hooks/use-date-format.ts"
import { cn } from "../../../../../utils/cn.ts"
import { StandaloneEventEditModal } from "../../../../Calendar/CalendarPage/components/CalendarView/StandaloneEventEditModal.tsx"
import { calendarDeepLink } from "../../../../Calendar/helpers/calendar-deep-link.ts"
import {
  resolveInPlaceEditEventId,
  selectCalendarEditAllowed
} from "../../../../Calendar/helpers/calendar-permissions.selectors.ts"
import { ActivityQuickDetailsPopover } from "../../../../Leads/components/ActivityQuickDetails/ActivityQuickDetailsPopover.tsx"
import {
  formatFollowUp,
  isFollowUpOverdue
} from "../../../../Leads/helpers/lead-table-format.ts"
import { LEAD_STATUSES } from "../../../constants/lead-statuses.ts"
import { fetchLead } from "../../../redux/lead.thunks.ts"
import { type Lead } from "../../../types/lead.types.ts"
import {
  LeadScheduleEditorModal,
  type LeadScheduleKind
} from "../../schedule/LeadScheduleEditorModal.tsx"
import { type LeadHeaderPatch } from "./lead-header-patch.ts"

const { Text } = Typography

interface ScheduleReadingProps {
  kind: LeadScheduleKind
  label: string
  value: string
  empty: string
  editLabel: string
  canEdit?: boolean
  // Undefined (not just omitted) when the caller can't edit — the popover
  // renders its own Edit button whenever it's handed one at all, regardless
  // of `canEdit`, so a defined-but-inert callback here left a click that
  // silently did nothing rather than no button at all.
  onEdit?: () => void
  testId: string
  eventId?: string | null
  overdue?: boolean
  calendarHref?: string | null
  onCompleted?: () => void
  /** Named by a rejected stage transition; the label carries the mark. */
  missing?: boolean
}

function ScheduleReading({
  kind,
  label,
  value,
  empty,
  editLabel,
  canEdit,
  onEdit,
  testId,
  eventId,
  overdue,
  calendarHref,
  onCompleted,
  missing
}: ScheduleReadingProps) {
  const [open, setOpen] = useState(false)

  return (
    <Flex
      vertical
      gap={2}
      data-testid={testId}
      data-missing={missing ? "true" : undefined}
      className={cn(
        missing && "border border-error bg-error-bg rounded-md px-2 py-1"
      )}
    >
      <Text type={missing ? "danger" : "secondary"} className={"text-xs"}>
        {label}
      </Text>
      <Flex gap={4} align={"center"}>
        {/* An unset optional field is muted, never an error reading — and
            offers no quick-details popover, since there is nothing to show. */}
        {value ? (
          <ActivityQuickDetailsPopover
            kind={kind}
            eventId={eventId}
            overdue={overdue}
            fallbackDateLabel={value}
            calendarHref={calendarHref}
            open={open}
            onOpenChange={setOpen}
            onEdit={onEdit}
            onCompleted={onCompleted}
          >
            <Text className={"cursor-pointer"}>{value}</Text>
          </ActivityQuickDetailsPopover>
        ) : (
          <Text type={"secondary"}>{empty}</Text>
        )}
        {canEdit && (
          <Button
            size={"small"}
            type={"text"}
            aria-label={editLabel}
            icon={<EditOutlined />}
            onClick={onEdit}
          />
        )}
      </Flex>
    </Flex>
  )
}

interface LeadWorkspaceScheduleProps {
  lead: Lead
  owners: BaseOptionType[]
  canEdit?: boolean
  saving?: boolean
  /** Named by a rejected stage transition; the appointment control carries the mark. */
  appointmentMissing?: boolean
  onChange: (patch: LeadHeaderPatch) => Promise<boolean>
  /** Fires after a follow-up is marked complete from the popover, so the page
   *  can reread the lead the same way it does after any other patch. */
  onActivityCompleted?: () => void
}

export function LeadWorkspaceSchedule({
  lead,
  owners,
  canEdit,
  saving,
  appointmentMissing,
  onChange,
  onActivityCompleted
}: LeadWorkspaceScheduleProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { userFormat, normalizeDate } = useDateFormat()
  const [editing, setEditing] = useState<LeadScheduleKind | null>(null)
  // The same in-place editor either reading opens, distinguished only by
  // which linked event id it's given — never both at once, since only one
  // ScheduleReading's Edit can be clicked at a time.
  const [standaloneEditEventId, setStandaloneEditEventId] = useState<
    string | null
  >(null)
  const calendarEditAllowed = useAppSelector(selectCalendarEditAllowed)

  const isRehash = lead?.status === LEAD_STATUSES.NURTURING

  const followUp = formatFollowUp(lead?.follow_up_date, lead?.follow_up_time, {
    isRehash,
    instant: lead?.follow_up_at,
    userFormat
  })
  const appointment = formatFollowUp(
    lead?.appointment_date,
    lead?.appointment_time,
    { instant: lead?.appointment_at, userFormat }
  )
  const followUpOverdue = isFollowUpOverdue(
    lead?.follow_up_date,
    undefined,
    userFormat
  )

  const appointmentDate = lead?.appointment_date
    ? normalizeDate(lead.appointment_date)
    : null
  const appointmentCalendarHref = appointmentDate
    ? calendarDeepLink(appointmentDate, lead?.appointment_event)
    : null

  const reloadLead = () => {
    dispatch(fetchLead(lead.id))
  }

  // See docs/kb/decisions/2026-09-13-appointment-and-follow-up-edit-mounts-in-place.md
  // — shared shape with the Leads Active table's own editLinkedEvent so the
  // two entry points can't drift the way the original bug did.
  const editLinkedEvent = (
    eventId: string | null | undefined,
    fallback: () => void
  ) => {
    const inPlaceId = resolveInPlaceEditEventId(
      canEdit,
      calendarEditAllowed,
      eventId
    )
    if (inPlaceId) {
      setEditing(null)
      setStandaloneEditEventId(inPlaceId)
      return
    }
    setStandaloneEditEventId(null)
    fallback()
  }

  return (
    <>
      <ScheduleReading
        kind={"followUp"}
        testId={"lead-header-follow-up"}
        label={t("leadWorkspace.header.followUp.label")}
        value={followUp}
        empty={t("leadWorkspace.header.followUp.none")}
        editLabel={t("leadWorkspace.header.followUp.edit")}
        canEdit={canEdit}
        eventId={lead?.follow_up_event}
        overdue={followUpOverdue}
        onEdit={
          canEdit
            ? () =>
                editLinkedEvent(lead?.follow_up_event, () =>
                  setEditing("followUp")
                )
            : undefined
        }
        onCompleted={onActivityCompleted}
      />

      <div className={"hidden w-px self-stretch bg-gray-200 sm:block"} />

      <ScheduleReading
        kind={"appointment"}
        testId={"lead-header-appointment"}
        label={t("leadWorkspace.header.appointment.label")}
        value={appointment}
        empty={t("leadWorkspace.header.appointment.none")}
        editLabel={t("leadWorkspace.header.appointment.edit")}
        canEdit={canEdit}
        missing={appointmentMissing}
        eventId={lead?.appointment_event}
        calendarHref={appointmentCalendarHref}
        onEdit={
          canEdit
            ? () =>
                editLinkedEvent(lead?.appointment_event, () =>
                  setEditing("appointment")
                )
            : undefined
        }
      />

      {canEdit && (
        <LeadScheduleEditorModal
          kind={editing}
          lead={lead}
          owners={owners}
          saving={saving}
          onChange={onChange}
          onClose={() => setEditing(null)}
        />
      )}

      {standaloneEditEventId && canEdit && (
        <StandaloneEventEditModal
          key={standaloneEditEventId}
          eventId={standaloneEditEventId}
          onSaved={reloadLead}
          onClose={() => setStandaloneEditEventId(null)}
        />
      )}
    </>
  )
}
